import { XIcon } from '@music-vine/cadence/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, DrawerContent, DrawerTrigger } from '@music-vine/cadence/ui';
import { useAnyPlayerActive } from '@/hooks/media/useAnyPlayerActive';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import { MobileBanner } from './MobileBanner';
import { setAsDismissedInStorage } from './utils/setAsDismissedInStorage';
import type { Variants } from 'motion/react';
import type { ReactNode } from 'react';
export type PopUpVariant = 'default' | 'banner-img-pu' | 'mobile-banner-image' | 'mobile-banner-dialog' | 'mobile-banner-default' | 'mobile-payment-issue';
export type PopUpPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'center';

/*
 The visibility of the popup is controlled by the these props
 in the following priority order:

 1. duration (set as undefined to show indefinitely)
 2. excludeSlugs (set to undefined or an empty array to show on all pages)
 3. forceShow (will not override the above, but if set to true,
 the popup will show even if it has been dismissed
 [4. storageKey (if the popup has been dismissed, i.e., there is an item
 in location storage with this prop as its name and a value of true, the popup will not show)]
 5. isHidden (set to true to hide the popup under certain conditions)
*/

export interface PagePopupProps {
  /**
   * Render `children` as-is instead of inside a PagePopup. The children then
   * own their own visibility, layout and dismissal.
   */
  bareChildren?: boolean;
  children: ReactNode;
  /**
   * Delay in milliseconds before the popup shows
   */
  delay?: number;
  /**
   * Apply `delay` on every page, not just the homepage
   */
  delayOnEveryPage?: boolean;
  dismissPermanently?: boolean;
  duration?: {
    from: Date;
    to: Date;
  };
  excludeSlugs?: (string | RegExp)[];
  forceShow?: boolean;
  /**
   * Only show on pages matching one of these (all pages when omitted)
   */
  includeSlugs?: (string | RegExp)[];
  isHidden?: boolean;
  mobileChildren?: ReactNode;
  /**
   * If true for the given screen size, the X icon for dismissing the popup will not be shown.
   */
  noXIcon?: {
    desktop?: boolean;
    mobile?: boolean;
  };
  onClickDismiss?: () => void;
  /**
   * Called when the popup is shown to the user
   */
  onShow?: () => void;
  position?: PopUpPosition;
  revalidateKey?: string;
  storageKey: string;
  storageType?: 'localStorage' | 'sessionStorage';
  variant?: PopUpVariant;
}
const getPositionClasses = (pos: PopUpPosition, anyPlayerActive?: boolean): string => {
  const basePositions = {
    'bottom-left': `left-10 ${anyPlayerActive ? 'bottom-24' : 'bottom-8'}`,
    'bottom-right': `right-10 ${anyPlayerActive ? 'bottom-24' : 'bottom-8'}`,
    'top-left': 'left-4 top-4',
    'top-right': 'right-4 top-4',
    center: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
  };
  return basePositions[pos] || 'right-4 top-4';
};
const defaultNoXIcon = {
  desktop: false,
  mobile: false
};
export const PagePopup = ({
  children,
  storageKey,
  storageType = 'localStorage',
  position = 'bottom-right',
  variant = 'default',
  onClickDismiss,
  onShow,
  noXIcon = defaultNoXIcon,
  mobileChildren,
  delay = 1000,
  delayOnEveryPage = false,
  forceShow,
  dismissPermanently = true
}: Omit<PagePopupProps, 'excludeSlugs' | 'includeSlugs' | 'duration'>) => {
  const [show, setShow] = useState<boolean>(false);
  const [isExiting, setIsExiting] = useState(false);
  const {
    isMd
  } = useTWScreens();
  const anyPlayerActive = useAnyPlayerActive();
  const {
    asPath
  } = useRouter();
  const popInVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: position.includes('bottom') ? 50 : -50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 20,
        duration: 0.15
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: position.includes('bottom') ? 50 : -50,
      transition: {
        duration: 0.15
      }
    }
  };
  const dismiss = useCallback(() => {
    setIsExiting(true);
    const timeoutId = setTimeout(() => {
      setShow(false);
      if (dismissPermanently) {
        setAsDismissedInStorage({
          key: storageKey,
          storage: storageType
        });
      }
      onClickDismiss?.();
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [onClickDismiss, storageKey, storageType, dismissPermanently]);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data.type === 'DISMISS_POPUP') {
        dismiss();
      }
      return;
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dismiss]);
  const handleDrawerClose = useCallback(() => {
    setShow(false);
    if (dismissPermanently) {
      setAsDismissedInStorage({
        key: storageKey,
        storage: storageType
      });
    }
    onClickDismiss?.();
    window.postMessage({
      type: 'DISMISS_POPUP'
    }, '*');
  }, [storageKey, storageType, onClickDismiss, dismissPermanently]);
  useEffect(() => {
    const actualDelay = delayOnEveryPage || asPath === '/' ? delay : 0;
    const timeoutId = setTimeout(() => {
      setShow(true);
    }, actualDelay);
    return () => clearTimeout(timeoutId);
  }, [delay, delayOnEveryPage, asPath]);
  useEffect(() => {
    if (show) {
      onShow?.();
    }
  }, [show, onShow]);
  if (!show) {
    return null;
  }
  if (!isMd) {
    if (mobileChildren) {
      return <MobileBanner anyPlayerActive={anyPlayerActive} isExiting={isExiting} noXIcon={noXIcon.mobile} onDismiss={dismiss} popInVariants={popInVariants} show={show}>
          {mobileChildren}
        </MobileBanner>;
    }
    return <Drawer onOpenChange={open => {
      if (!open) {
        handleDrawerClose();
      }
    }} open={show}>
        <DrawerTrigger>
          <span style={{
          display: 'none',
          zIndex: 1000
        }} />
        </DrawerTrigger>
        <DrawerContent>
          <div className={cn('w-full bg-white dark:bg-gray-950', {
          'p-0': variant === 'banner-img-pu',
          'px-0 pt-4 pb-0': variant === 'default',
          'shadow-[0px_0px_6px_1px_#F7F9F9] dark:shadow-[0px_0px_6px_1px_rgba(50,52,52,0.50)]': variant !== 'default'
        })}>
            <div className={cn('flex flex-col', {
            'items-center': variant !== 'default'
          })}>
              <Button className={cn('absolute top-2 right-2 z-10 m-2', variant === 'banner-img-pu' && 'text-white hover:text-gray-200', !!noXIcon.mobile && 'hidden')} onClick={handleDrawerClose} size="icon" variant="transparent">
                <span className="sr-only">dismiss popup</span>
                <XIcon className="h-4 w-4" />
              </Button>

              <div className={cn('w-full', {
              '[&_.popup-media]:w-full!': true,
              '[&_.popup-media]:px-0!': variant === 'default',
              'text-center': variant !== 'default'
            })}>
                {children}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>;
  }
  return <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="PagePopup" data-sentry-source-file="PagePopup.tsx">
      {!!show && !isExiting && <>
          {position === 'center' && <motion.div animate={{
        opacity: 1
      }} className="fixed inset-0 z-40 bg-black/50 dark:bg-black/70" exit={{
        opacity: 0
      }} initial={{
        opacity: 0
      }} />}

          <motion.div animate="visible" className={cn('fixed z-40 w-[320px]',
      // above the zones nav (z-20) and filter bar (z-[19]); also covers TopBar (z-30) at top positions
      getPositionClasses(position, anyPlayerActive))} data-page-popup exit="exit" initial="hidden" variants={popInVariants}>
            <div className={cn('relative overflow-hidden rounded-xl pb-7', 'bg-white dark:bg-gray-950', 'shadow-[0px_0px_15px_1px_rgba(35,37,37,0.08)] dark:shadow-[0px_0px_15px_1px_rgba(35,37,37,0.08)]', 'transition-opacity duration-700', 'sm:border-[1px] sm:border-gray-150 sm:border-solid dark:sm:border-gray-800', {
          'px-5 pt-7 pb-6': variant === 'default',
          'p-0': variant === 'banner-img-pu'
        })}>
              <Button className={cn('absolute top-2 right-2 z-10', variant === 'banner-img-pu' && 'text-white hover:text-gray-200', !!noXIcon.desktop && 'hidden')} onClick={dismiss} size="icon" variant="transparent">
                <span className="sr-only">dismiss popup</span>
                <XIcon className="h-4 w-4" />
              </Button>

              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};